import React, { useEffect, useState, useMemo } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import {
  FaCity,
  FaMapMarkedAlt,
  FaPlus,
  FaEdit,
  FaCheckCircle,
  FaTimesCircle,
  FaSyncAlt,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";

const THEME = {
  primary: "rgb(23, 44, 87)", // #172C57 - Used strictly for buttons, links, and text
  primaryLight: "rgba(23, 44, 87, 0.08)",
  actionBlue: "#2563EB",      // Normal blue for table action buttons
  actionBlueHover: "#1D4ED8",
};

const AllCities = () => {
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cityName, setCityName] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [isActive, setIsActive] = useState(1);
  const [editingId, setEditingId] = useState(null); // null = add mode
  const [saving, setSaving] = useState(false);

  const toList = (data) => (Array.isArray(data) ? data : data.data || []);

  const fetchCities = async () => {
    setLoading(true);
    try {
      const response = await fetch(SummaryApi.GetAllCities.url, {
        method: SummaryApi.GetAllCities.method,
      });
      if (!response.ok) throw new Error("Network error");
      setCities(toList(await response.json()));
    } catch (err) {
      toast.error("Failed to load cities");
    } finally {
      setLoading(false);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await fetch(SummaryApi.GetAllDisticts.url, {
        method: SummaryApi.GetAllDisticts.method,
      });
      if (!response.ok) throw new Error("Network error");
      setDistricts(toList(await response.json()));
    } catch (err) {
      toast.error("Failed to load districts");
    }
  };

  useEffect(() => {
    fetchCities();
    fetchDistricts();
  }, []);

  // use the joined district_name if the API sends it, otherwise look it up
  const getDistrictName = (city) =>
    city.district_name ||
    districts.find((d) => String(d.district_id) === String(city.district_id))
      ?.district_name ||
    "-";

  // active districts, plus the city's current district when editing (even if inactive)
  const districtOptions = districts.filter(
    (d) =>
      Number(d.is_active) === 1 || String(d.district_id) === String(districtId)
  );

  const openAddModal = () => {
    setEditingId(null);
    setCityName("");
    setDistrictId("");
    setIsActive(1);
    setIsModalOpen(true);
  };

  const openEditModal = (city) => {
    setEditingId(city.city_id);
    setCityName(city.city_name);
    setDistrictId(String(city.district_id));
    setIsActive(Number(city.is_active));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) setIsModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!districtId) {
      toast.warn("Please select a district");
      return;
    }
    if (!cityName.trim()) {
      toast.warn("Please enter city name");
      return;
    }

    const isEdit = editingId !== null;
    const api = isEdit ? SummaryApi.UpdateCity : SummaryApi.CreateCity;
    const payload = isEdit
      ? {
        city_id: editingId,
        district_id: districtId,
        city_name: cityName.trim(),
        is_active: isActive,
      }
      : { district_id: districtId, city_name: cityName.trim() };

    setSaving(true);
    try {
      const response = await fetch(api.url, {
        method: api.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok || result?.success === false) {
        toast.error(
          result?.message || `Could not ${isEdit ? "update" : "create"} city`
        );
      } else {
        toast.success(
          result?.message || `City ${isEdit ? "updated" : "created"}`
        );
        setIsModalOpen(false);
        fetchCities();
      }
    } catch (err) {
      toast.error(
        `Something went wrong while ${isEdit ? "updating" : "creating"} city`
      );
    } finally {
      setSaving(false);
    }
  };

  // KPI Summary Counts
  const stats = useMemo(() => {
    const total = cities.length;
    const active = cities.filter((c) => Number(c.is_active) === 1).length;
    const inactive = total - active;
    const uniqueDistricts = new Set(
      cities.map((c) => String(c.district_id)).filter(Boolean)
    ).size;
    return { total, active, inactive, uniqueDistricts };
  }, [cities]);

  return (
    <div className="min-h-screen space-y-4 text-slate-800">
      {/* Top Header Card (White BG, Primary Text & Button) */}
      <div className="bg-white rounded-xl border border-slate-200 px-5 py-4 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3.5">
          <div
            className="w-11 h-11 rounded-full bg-[#F0F5FF] border border-[#DBEAFE] flex items-center justify-center shrink-0"
            style={{ color: THEME.primary }}
          >
            <FaCity size={19} />
          </div>
          <div>
            <h1
              className="text-lg font-bold tracking-tight"
              style={{ color: THEME.primary }}
            >
              Cities Management
            </h1>
            <p className="text-xs text-slate-500">
              Overview and configuration of cities mapped across districts.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
          <button
            onClick={() => {
              fetchCities();
              fetchDistricts();
            }}
            disabled={loading}
            style={{ color: THEME.primary }}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold hover:bg-slate-50 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            title="Refresh Cities"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} size={11} />
            Refresh
          </button>

          <button
            onClick={openAddModal}
            style={{ backgroundColor: THEME.primary }}
            className="hover:opacity-90 text-white text-xs font-semibold px-4 py-2 rounded-lg shadow-sm transition-opacity flex items-center gap-1.5"
          >
            <FaPlus size={11} />
            Add City
          </button>
        </div>
      </div>

      {/* 4 Pastel KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        {/* Card 1: Blue */}
        <div className="bg-[#F0F5FF] border border-[#DBEAFE] rounded-xl p-4 flex items-center gap-3.5">
          <div className="bg-[#2563EB] w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
            <FaCity size={16} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600">Total Cities</p>
            <p
              className="text-2xl font-bold leading-tight my-0.5"
              style={{ color: THEME.primary }}
            >
              {stats.total}
            </p>
            <p className="text-[11px] text-slate-500">All configured cities</p>
          </div>
        </div>

        {/* Card 2: Emerald */}
        <div className="bg-[#ECFDF5] border border-[#D1FAE5] rounded-xl p-4 flex items-center gap-3.5">
          <div className="bg-[#10B981] w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
            <FaCheckCircle size={16} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600">Active Cities</p>
            <p
              className="text-2xl font-bold leading-tight my-0.5"
              style={{ color: THEME.primary }}
            >
              {stats.active}
            </p>
            <p className="text-[11px] text-slate-500">Currently enabled</p>
          </div>
        </div>

        {/* Card 3: Purple */}
        <div className="bg-[#F5F3FF] border border-[#EDE9FE] rounded-xl p-4 flex items-center gap-3.5">
          <div className="bg-[#7C3AED] w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
            <FaMapMarkedAlt size={16} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600">
              Mapped Districts
            </p>
            <p
              className="text-2xl font-bold leading-tight my-0.5"
              style={{ color: THEME.primary }}
            >
              {stats.uniqueDistricts}
            </p>
            <p className="text-[11px] text-slate-500">Districts with cities</p>
          </div>
        </div>

        {/* Card 4: Red */}
        <div className="bg-[#FEF2F2] border border-[#FEE2E2] rounded-xl p-4 flex items-center gap-3.5">
          <div className="bg-[#EF4444] w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
            <FaTimesCircle size={16} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600">
              Inactive Cities
            </p>
            <p
              className="text-2xl font-bold leading-tight my-0.5"
              style={{ color: THEME.primary }}
            >
              {stats.inactive}
            </p>
            <p className="text-[11px] text-slate-500">Currently disabled</p>
          </div>
        </div>
      </div>

      {/* Cities Table Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <h2 className="text-sm font-bold" style={{ color: THEME.primary }}>
              All Cities List
            </h2>
            <span
              style={{
                backgroundColor: THEME.primaryLight,
                color: THEME.primary,
              }}
              className="text-xs font-bold px-2.5 py-0.5 rounded-full"
            >
              {cities.length} Records
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-left">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th
                  className="px-5 py-3 text-xs font-semibold tracking-wide w-20"
                  style={{ color: THEME.primary }}
                >
                  S.No
                </th>
                <th
                  className="px-5 py-3 text-xs font-semibold tracking-wide"
                  style={{ color: THEME.primary }}
                >
                  District
                </th>
                <th
                  className="px-5 py-3 text-xs font-semibold tracking-wide"
                  style={{ color: THEME.primary }}
                >
                  City Name
                </th>
                <th
                  className="px-5 py-3 text-xs font-semibold tracking-wide"
                  style={{ color: THEME.primary }}
                >
                  Status
                </th>
                <th
                  className="px-5 py-3 text-xs font-semibold tracking-wide"
                  style={{ color: THEME.primary }}
                >
                  Created Date
                </th>
                <th
                  className="px-5 py-3 text-xs font-semibold tracking-wide text-center w-32"
                  style={{ color: THEME.primary }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-5 py-12 text-center">
                    <FaSpinner
                      className="animate-spin text-2xl mx-auto mb-2"
                      style={{ color: THEME.primary }}
                    />
                    <span className="text-xs text-slate-500 font-medium">
                      Loading cities...
                    </span>
                  </td>
                </tr>
              ) : cities.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-5 py-12 text-center">
                    <FaCity className="text-slate-300 text-3xl mx-auto mb-2" />
                    <p
                      className="text-sm font-semibold"
                      style={{ color: THEME.primary }}
                    >
                      No cities found
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Click "+ Add City" to create your first city.
                    </p>
                  </td>
                </tr>
              ) : (
                cities.map((c, i) => {
                  const activeStatus = Number(c.is_active) === 1;
                  return (
                    <tr
                      key={c.city_id || i}
                      className="hover:bg-slate-50/80 transition-colors"
                    >
                      <td className="px-5 py-3 text-xs font-medium text-slate-500">
                        {i + 1}
                      </td>
                      <td className="px-5 py-3 text-xs font-medium text-slate-700">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-[#F0F5FF] text-[#2563EB] border border-[#DBEAFE] text-xs font-medium">
                          {getDistrictName(c)}
                        </span>
                      </td>
                      <td
                        className="px-5 py-3 text-xs font-semibold"
                        style={{ color: THEME.primary }}
                      >
                        {c.city_name}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${activeStatus
                            ? "bg-[#ECFDF5] text-[#10B981] border-[#D1FAE5]"
                            : "bg-[#FEF2F2] text-[#EF4444] border-[#FEE2E2]"
                            }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${activeStatus ? "bg-[#10B981]" : "bg-[#EF4444]"
                              }`}
                          />
                          {activeStatus ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600">
                        {c.created_date || "N/A"}
                      </td>
                      <td className="px-5 py-3 text-center">
                        {/* 🔽 UPDATED: Normal blue color instead of primary */}
                        <button
                          onClick={() => openEditModal(c)}
                          style={{ backgroundColor: THEME.actionBlue }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlueHover)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlue)}
                          className="text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors shadow-sm inline-flex items-center gap-1.5"
                        >
                          <FaEdit size={11} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit City Modal (Clean White BG, Primary Text & Buttons) */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        ariaHideApp={false}
        className="bg-white w-full max-w-md mx-auto rounded-2xl border border-slate-200 shadow-xl overflow-hidden outline-none"
        overlayClassName="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center p-4 z-50"
      >
        {/* Clean White Modal Header */}
        <div className="px-5 py-4 bg-white border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-full bg-[#F0F5FF] border border-[#DBEAFE] flex items-center justify-center"
              style={{ color: THEME.primary }}
            >
              <FaCity size={14} />
            </div>
            <h3
              className="text-sm font-bold tracking-wide"
              style={{ color: THEME.primary }}
            >
              {editingId !== null ? "Edit City" : "Add New City"}
            </h3>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="text-slate-400 hover:text-slate-600 w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
          >
            <FaTimes size={13} />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 bg-white">
          <div>
            <label
              className="block text-xs font-semibold mb-1.5"
              style={{ color: THEME.primary }}
            >
              District <span className="text-red-500">*</span>
            </label>
            <select
              value={districtId}
              onChange={(e) => setDistrictId(e.target.value)}
              className="w-full bg-[#F8FAFC] focus:bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#172C57] cursor-pointer transition-all"
            >
              <option value="">-- Select District --</option>
              {districtOptions.map((d) => (
                <option key={d.district_id} value={String(d.district_id)}>
                  {d.district_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              className="block text-xs font-semibold mb-1.5"
              style={{ color: THEME.primary }}
            >
              City Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={cityName}
              onChange={(e) => setCityName(e.target.value)}
              placeholder="Enter city name"
              className="w-full bg-[#F8FAFC] focus:bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#172C57] transition-all"
            />
          </div>

          {editingId !== null && (
            <div>
              <label
                className="block text-xs font-semibold mb-1.5"
                style={{ color: THEME.primary }}
              >
                Status
              </label>
              <select
                value={isActive}
                onChange={(e) => setIsActive(Number(e.target.value))}
                className="w-full bg-[#F8FAFC] focus:bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#172C57] cursor-pointer transition-all"
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ backgroundColor: THEME.primary }}
              className="px-4 py-1.5 text-white text-xs font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-sm disabled:opacity-60 flex items-center gap-1.5"
              disabled={saving}
            >
              {saving && <FaSpinner className="animate-spin" size={11} />}
              {saving
                ? "Saving..."
                : editingId !== null
                  ? "Update City"
                  : "Save City"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AllCities;